import { z } from 'zod';

/**
 * Sections of the CRM and their permissions; the gateway enforces them. "manage"
 * includes viewing. Organization admins hold all, a member without a position views
 * everything; positions, employees and the telematics account are the admins'.
 */
export const permissionSections = [
  { key: 'equipment', view: 'equipment.view', manage: 'equipment.manage' },
  { key: 'rentals', view: 'rentals.view', manage: 'rentals.manage' },
  { key: 'warehouses', view: 'warehouses.view', manage: 'warehouses.manage' },
  { key: 'catalog', view: 'catalog.view', manage: 'catalog.manage' },
  { key: 'stock', view: 'stock.view', manage: 'stock.operate' },
  { key: 'objects', view: 'objects.view', manage: 'objects.manage' },
  { key: 'documents', view: 'documents.view', manage: 'documents.manage' },
  { key: 'telematics', view: 'telematics.view', manage: null },
] as const;
export type PermissionSection = (typeof permissionSections)[number];

export const permissions = [
  'equipment.view',
  'equipment.manage',
  'rentals.view',
  'rentals.manage',
  'warehouses.view',
  'warehouses.manage',
  'catalog.view',
  'catalog.manage',
  'stock.view',
  'stock.operate',
  'objects.view',
  'objects.manage',
  'documents.view',
  'documents.manage',
  'telematics.view',
] as const;
export type Permission = (typeof permissions)[number];
export const permissionSchema = z.enum(permissions);
export const viewPermissions = permissions.filter((item) => item.endsWith('.view'));

export const positionSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  description: z.string(),
  permissions: z.array(permissionSchema),
  memberCount: z.number().int(),
});
export type Position = z.infer<typeof positionSchema>;

export const createPositionSchema = z.object({
  name: z.string().trim().min(1, 'Введите название').max(200, 'Не длиннее 200 символов'),
  description: z.string().trim().max(1000, 'Не длиннее 1000 символов').default(''),
  permissions: z.array(permissionSchema).default([]),
});
export type CreatePosition = z.infer<typeof createPositionSchema>;

export const updatePositionSchema = createPositionSchema;
export type UpdatePosition = CreatePosition;

export const positionParamsSchema = z.object({
  organizationId: z.coerce.number().int().positive(),
  positionId: z.coerce.number().int().positive(),
});

/** Driver license categories; a license is recorded as "<kind>:<category>". */
export const driverLicenseCategories = {
  auto: [
    'A',
    'A1',
    'B',
    'B1',
    'BE',
    'C',
    'C1',
    'CE',
    'C1E',
    'D',
    'D1',
    'DE',
    'D1E',
    'M',
    'Tm',
    'Tb',
  ],
  tractor: ['AI', 'AII', 'AIII', 'AIV', 'B', 'C', 'D', 'E', 'F'],
} as const;
export type DriverLicenseKind = keyof typeof driverLicenseCategories | 'special';

const knownLicense = (kind: keyof typeof driverLicenseCategories, category: string) =>
  (driverLicenseCategories[kind] as readonly string[]).includes(category);

export const driverLicenseSchema = z
  .string()
  .trim()
  .refine((code) => {
    const [kind, ...rest] = code.split(':');
    const category = rest.join(':').trim();
    if (kind === 'special') return category.length > 0 && category.length <= 80;
    return (kind === 'auto' || kind === 'tractor') && knownLicense(kind, category);
  }, 'Неизвестная категория прав');

export const phoneSchema = z
  .string()
  .trim()
  .regex(/^(\+?[0-9][0-9 ()-]{4,30})?$/, 'Телефон: цифры, пробелы, скобки, дефис и +');

/** What the organization records about an employee besides the account. */
export const employeeCardSchema = z.object({
  positionId: z.number().int().positive().nullable().default(null),
  phone: phoneSchema.default(''),
  driverLicenses: z.array(driverLicenseSchema).max(30).default([]),
});
export type EmployeeCard = z.infer<typeof employeeCardSchema>;
